import { Link } from "react-router-dom";
import { Check } from "lucide-react";
import { useLang, pick } from "@/lib/i18n";
import { useSite } from "@/lib/site";
import { useAuth } from "@/lib/auth";
import { money } from "@/lib/api";
import { SectionHead } from "@/components/site/Contact";

export function PlanCard({ plan, idx, action }) {
  const { t, lang } = useLang();
  const feats = plan[`features_${lang}`]?.length ? plan[`features_${lang}`] : plan.features_fr || [];
  return (
    <div data-testid={`plan-card-${idx}`} className={`panel panel-hover relative flex flex-col p-6 sm:p-8 ${plan.highlight ? "border-red-500/50 shadow-[0_0_60px_-20px_rgba(229,9,20,0.6)]" : ""}`}>
      {plan.highlight && <span className="absolute -top-3 left-6 rounded-full bg-red-600 px-3 py-1 font-mono text-[10px] uppercase tracking-widest text-white">{t("plans.popular")}</span>}
      <h3 className="font-display text-3xl uppercase text-white">{pick(plan, "name", lang)}</h3>
      {pick(plan, "desc", lang) && <p className="mt-2 text-sm text-slate-400">{pick(plan, "desc", lang)}</p>}
      <div className="mt-6" data-testid={`plan-price-${idx}`}>
        {plan.price_cad != null ? (
          <p><span className="font-display text-5xl text-white">{money(plan.price_cad, lang)}</span> <span className="text-sm text-slate-400">{t("plans.perMonth")} + {t("plans.taxShort")}</span></p>
        ) : (
          <p className="font-mono text-sm uppercase tracking-widest text-slate-400">{t("plans.soon")}</p>
        )}
        <p className="mt-2 text-sm text-slate-300" data-testid={`plan-hours-${idx}`}>
          {plan.monthly_hours != null ? <><span className="font-mono text-red-400">{plan.monthly_hours}</span> {t("plans.hours")}</> : t("plans.hoursSoon")}
        </p>
      </div>
      <ul className="mt-6 flex-1 space-y-2">
        {feats.map((f, i) => <li key={i} className="flex gap-2 text-sm text-slate-300"><Check size={16} className="mt-0.5 shrink-0 text-red-500" />{f}</li>)}
      </ul>
      <div className="mt-8">{action}</div>
    </div>
  );
}

export default function Plans() {
  const { t, lang } = useLang();
  const { site } = useSite();
  const { user } = useAuth();
  const plans = site?.plans || [];
  const pricing = site?.pricing || {};
  const open = site?.capacity?.sales_open !== false;
  const to = (p) => (user ? (user.role === "admin" ? "/admin" : `/member?plan=${p.id}`) : `/register?plan=${p.id}`);

  return (
    <section id="memberships" className="border-y border-white/5 bg-[#0c0d11]" data-testid="memberships-section">
      <div className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-10 lg:py-28">
        <SectionHead eyebrow={t("plans.eyebrow")} title={t("plans.title")} sub={t("plans.sub")} />
        {!open && <p className="mb-8 rounded-xl border border-amber-500/40 bg-amber-950/30 p-4 text-amber-200" data-testid="sales-paused-banner">{t("plans.paused")}</p>}
        <div className="grid gap-5 md:grid-cols-3">
          {plans.map((p, i) => (
            <PlanCard key={p.id} plan={p} idx={i} action={
              <Link to={to(p)} data-testid={`plan-cta-${i}`}
                className={`block rounded-full px-5 py-3 text-center font-semibold ${open ? "btn-red" : "pointer-events-none border border-white/10 text-slate-500"}`}>
                {t("plans.cta")}
              </Link>} />
          ))}
        </div>
        <div className="mt-8 grid gap-3 md:grid-cols-2" data-testid="plans-notes">
          <div className="panel p-5 md:col-span-2">
            <p className="font-semibold text-white" data-testid="plans-access-note">{t("plans.access")}</p>
            <p className="mt-2 text-sm text-red-300" data-testid="plans-members-only">{t("plans.membersOnly")}</p>
            <p className="mt-1 text-sm text-slate-400">{t("plans.selfService")}</p>
          </div>
          <div className="panel p-5" data-testid="plans-extra-hour"><p className="font-mono text-[11px] uppercase tracking-widest text-slate-500">{t("plans.extra")}</p><p className="mt-2 text-xl text-white">{money(pricing.extra_hour_price_cad ?? 30, lang)} / h</p><p className="mt-1 text-xs text-slate-500">{t("plans.extraHow")}</p></div>
          <div className="panel p-5" data-testid="plans-renewal"><p className="font-mono text-[11px] uppercase tracking-widest text-slate-500">{t("plans.renewalTitle")}</p><p className="mt-2 text-sm text-slate-300">{t("plans.renewal")}</p></div>
        </div>
        <p className="mt-6 font-mono text-xs text-slate-400" data-testid="plans-tax-note">CAD · {t("plans.taxes")}</p>
      </div>
    </section>
  );
}
